import { useEffect, useState } from 'react'
import { Download, Trash2 } from 'lucide-react'
import { Alert, Button, Card, CardBody, CardHeader, Input, SkeletonList, Switch } from '@/components/ui'
import { ConfirmDialog, QueryBoundary } from '@/components/domain'
import { useDisclosure } from '@/hooks'
import { useT } from '@/internationalization/i18n'
import { toast } from '@/stores'
import { useRequestExport, useSecuritySettings } from '../hooks'
import type { SecuritySettings as Security } from '@/types'

/**
 * Deleting a workspace is the most destructive action in the product, so it
 * lives at the bottom of the last settings pane, in its own bordered card,
 * behind a confirm that ignores overlay clicks.
 */
export function SecuritySettings() {
  const t = useT()
  const { query, save } = useSecuritySettings()
  const requestExport = useRequestExport()
  const [draft, setDraft] = useState<Security | null>(null)
  const ssoDialog = useDisclosure()
  const deleteDialog = useDisclosure()

  useEffect(() => {
    if (query.data) setDraft(query.data)
  }, [query.data])

  return (
    <QueryBoundary
      isLoading={query.isPending || !draft}
      error={query.error}
      onRetry={query.refetch}
      skeleton={<SkeletonList rows={4} className="sb-panel p-4" />}
    >
      {draft && (
        <div className="space-y-4">
          <Card>
            <CardHeader title={t('security.authTitle')} description={t('security.authDesc')} />
            <CardBody className="space-y-4">
              <Switch
                label={t('security.requireSso')}
                description={t('security.requireSsoDesc')}
                showLabel
                checked={draft.requireSso}
                onChange={(requireSso) => {
                  // Turning this on can lock the owner out if their IdP mapping
                  // is wrong, so it confirms; turning it off cannot.
                  if (requireSso) {
                    ssoDialog.open()
                    return
                  }
                  setDraft({ ...draft, requireSso })
                  save.mutate({ requireSso })
                }}
              />
              <Switch
                label={t('security.enforce2fa')}
                showLabel
                checked={draft.enforceTwoFactorForAdmins}
                onChange={(enforceTwoFactorForAdmins) => {
                  setDraft({ ...draft, enforceTwoFactorForAdmins })
                  save.mutate({ enforceTwoFactorForAdmins })
                }}
              />
              <Input
                label={t('security.samlUrl')}
                value={draft.samlMetadataUrl ?? ''}
                onChange={(event) => setDraft({ ...draft, samlMetadataUrl: event.target.value })}
                onBlur={() => save.mutate({ samlMetadataUrl: draft.samlMetadataUrl })}
              />
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={t('security.retentionTitle')}
              description={t('security.retentionDesc')}
            />
            <CardBody className="grid gap-4 sm:grid-cols-3">
              {[
                [t('security.retention.audio'), t('security.retention.days', { count: draft.retention.audioDays })],
                [t('security.retention.transcript'), t('security.retention.months', { count: Math.round(draft.retention.transcriptDays / 30) })],
                [t('security.retention.summary'), draft.retention.summaryDays ? t('security.retention.days', { count: draft.retention.summaryDays }) : t('security.retention.indefinite')],
              ].map(([label, value]) => (
                <div key={label} className="rounded-md border border-border p-3">
                  <p className="text-sm text-fg-muted">{label}</p>
                  <p className="mt-0.5 text-md font-semibold text-fg">{value}</p>
                </div>
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title={t('security.exportTitle')}
              description={t('security.exportDesc')}
            />
            <CardBody>
              <Button
                leftIcon={<Download className="h-4 w-4" />}
                isLoading={requestExport.isPending}
                onClick={() => requestExport.mutate()}
              >
                {t('security.requestExport')}
              </Button>
            </CardBody>
          </Card>

          <Card className="border-danger/30">
            <CardHeader title={t('security.dangerZone')} description={t('security.dangerZoneDesc')} />
            <CardBody className="space-y-3">
              <Alert tone="danger" title={t('security.deleteWorkspaceAlertTitle')}>
                {t('security.deleteWorkspaceAlertBody')}
              </Alert>
              <Button variant="danger" leftIcon={<Trash2 className="h-4 w-4" />} onClick={deleteDialog.open}>
                {t('security.deleteWorkspace')}
              </Button>
            </CardBody>
          </Card>

          <ConfirmDialog
            isOpen={ssoDialog.isOpen}
            onCancel={ssoDialog.close}
            onConfirm={() => {
              ssoDialog.close()
              setDraft({ ...draft, requireSso: true })
              save.mutate({ requireSso: true })
            }}
            title={t('security.ssoConfirmTitle')}
            description={t('security.ssoConfirmBody')}
            confirmLabel={t('security.ssoConfirmButton')}
            isDestructive
          />

          <ConfirmDialog
            isOpen={deleteDialog.isOpen}
            onCancel={deleteDialog.close}
            onConfirm={() => {
              deleteDialog.close()
              toast.error(t('security.toast.deletionDisabled'))
            }}
            title={t('security.deleteConfirmTitle')}
            description={t('security.deleteConfirmBody')}
            confirmLabel={t('security.deleteConfirmButton')}
            isDestructive
          />
        </div>
      )}
    </QueryBoundary>
  )
}
